<template>
	<div class="action-bar">
		<div class="action-update">
			<template v-if="isEditActive">
				<v-btn class="primary" @click="confirmAction">{{ confirmText }}</v-btn>
				<v-btn text @click="cancelAction" class="cancel-changes-button">{{ cancelText }}</v-btn>
			</template>
			<template v-else>
				<v-btn outlined @click="updateAction">{{ updateText }}</v-btn>
			</template>
		</div>

		<template v-if="(deleteAllowed && !isEditActive)">
			<div class="action-disable">
				<template v-if="isDisabled">
					<modal-dialog 
						title="Are you sure?" 
						:closeDiscard="toggleDialog"
						:executeText='deleteExecuteText'
						:executeAction="deleteAction"
						:showDialog="showDialog"
					>
						<div slot="activator">
							<v-btn text class="dangerous" @click="toggleDialog">
								<v-icon>mdi-delete-outline</v-icon>Delete Forever
							</v-btn>
						</div>
						<div slot="content">
							<p>{{ deleteMessage }}</p>
						</div>
					</modal-dialog>
					<v-btn class="secondary" @click="enableAction">{{ enableText }}</v-btn>
				</template>
				<template v-else>
					<v-btn text class="dangerous" @click="disableAction">{{ disableText }}</v-btn>
				</template>
			</div>
		</template>
		<template v-else-if="disableWithoutDelete && !isEditActive">
			<div class="action-disable">
				<template v-if="isDisabled">
					<v-btn class="secondary" @click="enableAction">{{ enableText }}</v-btn>
				</template>
				<template v-else>
					<v-btn text class="dangerous" @click="disableAction">{{ disableText }}</v-btn>
				</template>
			</div>
		</template>
		
	</div>
</template>

<script>
	/**
	 * @fileoverview edit-action-bar.vue displays an action bar that allows users to update, save, cancel, enable, disable,
	 * and sometimes delete an item from a list.
	 * 
	 * @author Sophia Frankel <sophia.frankel@quavo.com>
	 */
	export default {
		props: {
			/**
			 * Currently editing item
			 */
			isEditActive: {
				// are you currently in editing mode?
				type: Boolean,
				default: false
			},
			/**
			 * Confirmation text
			 */
			confirmText: {
				type: String,
				default: "Save Changes"
			},
			/**
			 * Update/Edit text
			 */
			updateText: {
				type: String,
				default: "Update"
			},
			/**
			 * Cancel text
			 */
			cancelText: {
				type: String,
				default: "Cancel Changes"
			},
			/**
			 * Confirmation function
			 */
			confirmAction: {
				type: Function,
				required: true
			},
			/**
			 * Update/Edit function
			 */
			updateAction: {
				type: Function,
				required: true
			},
			/**
			 * Cancel function
			 */
			cancelAction: {
				type: Function,
				required: true
			},
			/**
			 * Delete allowed flag
			 */
			deleteAllowed: {
				type: Boolean,
				default: false
			},
			/**
			 * Delete message
			 */
			deleteMessage: {
				type: String
			},
			/**
			 * Delete execute text
			 */
			deleteExecuteText: {
				type: String
			},
			/**
			 * Delete function
			 */
			deleteAction: {
				type: Function
			},
			/**
			 * Flag for allowing disabling without allowing deleting
			 */
			disableWithoutDelete: {
				type: Boolean,
				default: false
			},
			/**
			 * Enable text
			 */
			enableText: {
				type: String,
				default: "Enable"
			},
			/**
			 * Disable text
			 */
			disableText: {
				type: String,
				default: "Disable"
			},
			/**
			 * Enable function
			 */
			enableAction: {
				type: Function
			},
			/**
			 * Disable function
			 */
			disableAction: {
				type: Function
			},
			/**
			 * Disabled flag
			 */
			isDisabled: {
				type: Boolean
			}
		},
		data() {
			return {
				showDialog: false	// show delete dialog
			}
		},
		methods: {
			/**
			 * Toggle the showDialog flag
			 */
			toggleDialog() {
				this.showDialog = !this.showDialog;
			},
		}
	}
</script>

<style lang="scss">
	@import '@/design/components/_edit-action-bar.scss';
</style>